import { Injectable } from '@angular/core';
import { Identity, NoIdentity } from '@rivethealth/data/auth';
import { Observable, of } from 'rxjs';
import { catchError, filter, map, shareReplay } from 'rxjs/operators';
import { AuthService as ApiAuthService } from '../../api/auth.service';
import { AuthService } from '../../common/auth/auth.service';
import { useSubscriptionLatest } from '../../common/core/hooks';
import { loadMap, success } from '../../common/helpers/loadable';

@Injectable({ providedIn: 'root' })
export class IdentityState {
  constructor(
    private readonly authService: ApiAuthService,
    private readonly auth: AuthService,
  ) {}

  readonly load = this.auth.authentication.pipe(
    loadMap(() => this.authService.getIdentity()),
  );

  readonly value: Observable<Identity> = this.load.pipe(
    success(),
    filter(i => i != null),
    shareReplay(1),
  );

  // this is used to give a no Auth for intercom. Our site relies on errors
  // for admins to log in.
  readonly loadNoError = this.auth.authentication.pipe(
    loadMap(token => {
      if (token) {
        return this.authService.getIdentity().pipe(
          catchError(() => {
            return of({ id: null, type: 'unauthorized' } as NoIdentity);
          }),
        );
      } else {
        return of({ id: null, type: 'unauthorized' } as NoIdentity);
      }
    }),
  );

  readonly noErrorValue: Observable<Identity | NoIdentity> =
    this.loadNoError.pipe(success(), shareReplay(1));

  readonly nonSystemUserId: Observable<number | undefined> = this.value.pipe(
    map(identity => (identity.type === 'user' ? identity.id : undefined)),
  );

  readonly userId: Observable<number> = this.value.pipe(
    map(identity => identity.id),
  );

  readonly isRiveter = this.value.pipe(map(i => i.type == 'system'));
}

export function useUserId(identity: IdentityState) {
  return useSubscriptionLatest(identity.userId, []);
}

export function useIsRiveter(identity: IdentityState) {
  return useSubscriptionLatest(identity.isRiveter, []) || false;
}
